/**
 * CR315: Pagination Configuration Section
 * 
 * Controls for pagination mode and page size (editable, default 50).
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Layers } from "lucide-react";
import type { ResultsBlockConfig } from "@/lib/layout-studio/types";

interface PaginationSectionProps {
  pagination: "pages" | "infinite";
  pageSize: number;
  onChange: (updates: Partial<Pick<ResultsBlockConfig, "pagination" | "pageSize">>) => void;
}

export function PaginationSection({ pagination, pageSize, onChange }: PaginationSectionProps) {
  const handlePaginationChange = (value: "pages" | "infinite") => {
    onChange({ pagination: value });
  };

  const handlePageSizeChange = (value: string) => {
    const num = parseInt(value, 10);
    if (!isNaN(num) && num >= 1 && num <= 200) {
      onChange({ pageSize: num });
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Layers className="h-4 w-4" />
          Pagination
        </CardTitle>
        <CardDescription>
          Configure how results are paginated.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Pagination Mode */}
        <div className="space-y-2">
          <Label className="text-xs">Pagination Mode</Label>
          <Select
            value={pagination}
            onValueChange={(v) => handlePaginationChange(v as "pages" | "infinite")}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pages">Pages</SelectItem>
              <SelectItem value="infinite">Infinite Scroll</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Page Size */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-xs">Page Size</Label>
            <span className="text-[10px] text-muted-foreground">
              (1–200)
            </span>
          </div>
          <Input
            type="number"
            value={pageSize}
            onChange={(e) => handlePageSizeChange(e.target.value)}
            min={1}
            max={200}
            className="h-8 text-xs"
          />
          <p className="text-[10px] text-muted-foreground">
            Number of deals to show per page. Default is 50.
          </p>
        </div>

        {/* Quick presets */}
        <div className="space-y-2">
          <Label className="text-[10px] text-muted-foreground">Quick presets</Label>
          <div className="flex flex-wrap gap-1">
            {[12, 24, 50, 100].map((preset) => (
              <button
                key={preset}
                type="button"
                onClick={() => onChange({ pageSize: preset })}
                className={`px-2 py-1 text-[10px] rounded border transition-colors ${
                  pageSize === preset
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background hover:bg-muted border-input"
                }`}
              >
                {preset}
              </button>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
